/**
 * Nestico Theme Enhancements — nestico-enhancements.js
 * Pakistani Household Accessories Store | Dawn-based theme
 * Vanilla JS, no jQuery dependency
 */

(function () {
  'use strict';

  /* ============================================================
     UTILITIES
     ============================================================ */

  /**
   * Wait for an element matching `selector` to appear in the DOM.
   * Returns a Promise that resolves with the element.
   */
  function waitForElement(selector, timeout) {
    timeout = timeout || 5000;
    return new Promise(function (resolve, reject) {
      var el = document.querySelector(selector);
      if (el) { resolve(el); return; }

      var observer = new MutationObserver(function () {
        var found = document.querySelector(selector);
        if (found) {
          observer.disconnect();
          resolve(found);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });

      setTimeout(function () {
        observer.disconnect();
        reject(new Error('Timeout waiting for: ' + selector));
      }, timeout);
    });
  }

  /**
   * Add a class to an element, then remove it after the CSS
   * animation/transition completes (or after `fallbackMs`).
   */
  function addTransientClass(el, className, fallbackMs) {
    fallbackMs = fallbackMs || 1000;
    el.classList.add(className);
    var timer = setTimeout(function () {
      el.classList.remove(className);
    }, fallbackMs);

    function onEnd() {
      clearTimeout(timer);
      el.classList.remove(className);
      el.removeEventListener('animationend', onEnd);
    }
    el.addEventListener('animationend', onEnd);
  }

  /** Safely format a price string (already formatted by Liquid, pass-through) */
  function getTextContent(el) {
    return el ? el.textContent.trim() : '';
  }

  /* ============================================================
     1. STICKY ADD-TO-CART BAR
     ============================================================ */

  var NesticoStickyATC = {
    bar: null,
    mainForm: null,
    mainBtn: null,
    titleEl: null,
    priceEl: null,
    stickyBtn: null,
    stickyTitle: null,
    stickyPrice: null,
    observer: null,

    init: function () {
      var self = this;

      // Only run on product pages
      var isProduct =
        document.body.classList.contains('template-product') ||
        document.querySelector('.product-form__submit') ||
        document.querySelector('product-form');

      if (!isProduct) return;

      self.bar = document.querySelector('.nestico-sticky-atc');
      if (!self.bar) return;

      self.mainForm =
        document.querySelector('product-form form') ||
        document.querySelector('.product-form form') ||
        document.querySelector('form[action="/cart/add"]');
      self.mainBtn =
        document.querySelector('.product-form__submit') ||
        document.querySelector('[name="add"]');

      self.stickyBtn   = self.bar.querySelector('.nestico-sticky-atc__btn');
      self.stickyTitle = self.bar.querySelector('.nestico-sticky-atc__title');
      self.stickyPrice = self.bar.querySelector('.nestico-sticky-atc__price');

      // Populate initial content
      self._syncContent();

      // Watch variant changes (Dawn dispatches 'variant:change' events)
      document.addEventListener('variant:change', function (e) {
        self._onVariantChange(e);
      });

      // Also listen for native Dawn change on product-info
      document.addEventListener('change', function (e) {
        if (e.target && e.target.closest('product-form, .product-form')) {
          setTimeout(function () { self._syncContent(); }, 100);
        }
      });

      // IntersectionObserver to show/hide sticky bar
      if (self.mainBtn && 'IntersectionObserver' in window) {
        self.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                self._hide();
              } else {
                self._show();
              }
            });
          },
          { threshold: 0.1, rootMargin: '0px 0px -20px 0px' }
        );
        self.observer.observe(self.mainBtn);
      }

      // Sticky button click: submit the main product form
      if (self.stickyBtn) {
        self.stickyBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self._submitMainForm();
        });
      }
    },

    _show: function () {
      if (this.bar) this.bar.classList.add('is-visible');
    },

    _hide: function () {
      if (this.bar) this.bar.classList.remove('is-visible');
    },

    _syncContent: function () {
      var self = this;

      // Product title
      var titleSource =
        document.querySelector('.product__title h1') ||
        document.querySelector('.product__title') ||
        document.querySelector('h1.product__title') ||
        document.querySelector('[data-product-title]');

      if (titleSource && self.stickyTitle) {
        self.stickyTitle.textContent = getTextContent(titleSource);
      }

      // Price
      var priceSource =
        document.querySelector('.price__regular .price-item--regular') ||
        document.querySelector('.price-item--regular') ||
        document.querySelector('.price__sale .price-item--sale') ||
        document.querySelector('[data-product-price]');

      if (priceSource && self.stickyPrice) {
        self.stickyPrice.textContent = getTextContent(priceSource);
      }

      // Button state (sold out / unavailable)
      if (self.mainBtn && self.stickyBtn) {
        var disabled = self.mainBtn.disabled || self.mainBtn.hasAttribute('disabled');
        self.stickyBtn.disabled = disabled;
        self.stickyBtn.textContent = disabled
          ? (self.mainBtn.textContent.trim() || 'Sold Out')
          : 'Add to Cart';
      }
    },

    _onVariantChange: function (e) {
      var self = this;
      var detail = e.detail || {};
      var variant = detail.variant;

      if (!variant) { self._syncContent(); return; }

      // Update price from variant data
      if (self.stickyPrice) {
        if (variant.price) {
          // Format cents to currency — simple approach, Shopify passes formatted money via detail
          var formatted =
            detail.html && detail.html.price
              ? detail.html.price
              : null;

          if (formatted) {
            // Extract text from HTML string
            var temp = document.createElement('div');
            temp.innerHTML = formatted;
            self.stickyPrice.textContent = getTextContent(temp);
          } else {
            self._syncContent();
          }
        }
      }

      // Available state
      if (self.stickyBtn) {
        self.stickyBtn.disabled = !variant.available;
        self.stickyBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }
    },

    _submitMainForm: function () {
      if (!this.mainForm) return;

      // Check required variant is selected
      var unselected = this.mainForm.querySelector(
        'input[type="radio"]:required:not(:checked), select[required] option[value=""]:checked'
      );

      if (unselected) {
        // Scroll to the main form and highlight
        this.mainForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Submit via the main button so Dawn's custom element handlers run
      if (this.mainBtn) {
        this.mainBtn.click();
      } else {
        this.mainForm.submit();
      }
    }
  };


  /* ============================================================
     2. ATC ATTENTION ANIMATION
     ============================================================ */

  var NesticoATCAttention = {
    init: function () {
      var isProduct =
        document.body.classList.contains('template-product') ||
        document.querySelector('.product-form__submit');

      if (!isProduct) return;

      var btn = document.querySelector('.product-form__submit');
      if (!btn) return;

      // Only fire if user hasn't already interacted
      var fired = false;

      function fireAttention() {
        if (fired) return;
        fired = true;
        addTransientClass(btn, 'nestico-atc-attention', 900);
      }

      // Fire shake after 3 s on page load
      setTimeout(fireAttention, 3000);

      // Also fire pulse whenever a variant changes
      document.addEventListener('variant:change', function () {
        addTransientClass(btn, 'nestico-atc-pulse', 700);
      });
    }
  };


  /* ============================================================
     3. TRACK ORDER TABS
     ============================================================ */

  var NesticoTrackOrder = {
    init: function () {
      var sections = document.querySelectorAll('.nestico-track-order');
      if (!sections.length) return;

      sections.forEach(function (section) {
        NesticoTrackOrder._initSection(section);
      });
    },

    _initSection: function (section) {
      var tabs   = section.querySelectorAll('.nestico-track-order__tab');
      var panels = section.querySelectorAll('.nestico-track-order__panel');

      if (!tabs.length || !panels.length) return;

      // Restore last active tab from sessionStorage
      var storageKey = 'nestico-track-tab-' + (section.dataset.sectionId || 'default');
      var lastTab = sessionStorage.getItem(storageKey);

      // Activate initial tab
      var initialIndex = 0;
      if (lastTab !== null) {
        var idx = parseInt(lastTab, 10);
        if (!isNaN(idx) && idx >= 0 && idx < tabs.length) {
          initialIndex = idx;
        }
      }
      NesticoTrackOrder._activateTab(tabs, panels, initialIndex);

      // Click handlers
      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          NesticoTrackOrder._activateTab(tabs, panels, i);
          try { sessionStorage.setItem(storageKey, String(i)); } catch (_) {}
        });

        // Keyboard: arrow keys
        tab.addEventListener('keydown', function (e) {
          var current = Array.prototype.indexOf.call(tabs, document.activeElement);
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            var next = (current + 1) % tabs.length;
            tabs[next].focus();
            NesticoTrackOrder._activateTab(tabs, panels, next);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            var prev = (current - 1 + tabs.length) % tabs.length;
            tabs[prev].focus();
            NesticoTrackOrder._activateTab(tabs, panels, prev);
          }
        });
      });

      // Form submissions
      var forms = section.querySelectorAll('.nestico-track-order__form');
      forms.forEach(function (form) {
        form.addEventListener('submit', function (e) {
          NesticoTrackOrder._handleSubmit(e, form, section);
        });
      });
    },

    _activateTab: function (tabs, panels, activeIndex) {
      tabs.forEach(function (tab, i) {
        var isActive = i === activeIndex;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      panels.forEach(function (panel, i) {
        panel.classList.toggle('is-active', i === activeIndex);
        panel.setAttribute('aria-hidden', String(i !== activeIndex));
      });
    },

    _handleSubmit: function (e, form, section) {
      var action = form.getAttribute('action');

      // If the form has a real action URL, let it submit naturally
      if (action && action !== '#' && action !== '') return;

      // Otherwise prevent and show a message / redirect
      e.preventDefault();

      var inputs = form.querySelectorAll('.nestico-track-order__input');
      var allFilled = Array.prototype.every.call(inputs, function (inp) {
        return inp.value.trim() !== '';
      });

      if (!allFilled) {
        inputs.forEach(function (inp) {
          if (!inp.value.trim()) {
            inp.style.borderColor = '#d32f2f';
            inp.focus();
          } else {
            inp.style.borderColor = '';
          }
        });
        return;
      }

      // Build query string and redirect
      var trackingUrl =
        section.dataset.trackingUrl ||
        form.dataset.trackingUrl ||
        '/pages/track-order';

      var params = new URLSearchParams();
      inputs.forEach(function (inp) {
        if (inp.name) params.set(inp.name, inp.value.trim());
      });

      window.location.href = trackingUrl + '?' + params.toString();
    }
  };


  /* ============================================================
     4. FAQ ACCORDION (fallback for browsers without details/summary)
     ============================================================ */

  var NesticoFAQ = {
    init: function () {
      var items = document.querySelectorAll('.nestico-faq-item');
      if (!items.length) return;

      items.forEach(function (item) {
        var question = item.querySelector('.nestico-faq-question');
        var answer   = item.querySelector('.nestico-faq-answer');

        if (!question || !answer) return;

        // Set initial ARIA state
        var isOpen = question.getAttribute('aria-expanded') === 'true';
        question.setAttribute('aria-expanded', String(isOpen));
        answer.setAttribute('aria-hidden', String(!isOpen));

        if (isOpen) {
          NesticoFAQ._open(answer);
        } else {
          answer.style.maxHeight = '0';
        }

        question.addEventListener('click', function () {
          var expanded = question.getAttribute('aria-expanded') === 'true';
          NesticoFAQ._toggle(question, answer, !expanded);
        });

        // Keyboard support
        question.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            question.click();
          }
        });
      });
    },

    _toggle: function (question, answer, shouldOpen) {
      question.setAttribute('aria-expanded', String(shouldOpen));
      answer.setAttribute('aria-hidden', String(!shouldOpen));

      if (shouldOpen) {
        NesticoFAQ._open(answer);
      } else {
        NesticoFAQ._close(answer);
      }
    },

    _open: function (answer) {
      answer.classList.add('is-open');
      // Measure natural height
      answer.style.maxHeight = 'none';
      var fullHeight = answer.scrollHeight + 'px';
      answer.style.maxHeight = '0';

      // Force reflow
      answer.offsetHeight; // eslint-disable-line no-unused-expressions

      answer.style.maxHeight = fullHeight;

      answer.addEventListener('transitionend', function onEnd() {
        answer.style.maxHeight = 'none'; // allow dynamic content resize
        answer.removeEventListener('transitionend', onEnd);
      });
    },

    _close: function (answer) {
      // Collapse from current height to 0
      var currentHeight = answer.scrollHeight + 'px';
      answer.style.maxHeight = currentHeight;

      // Force reflow
      answer.offsetHeight; // eslint-disable-line no-unused-expressions

      answer.style.maxHeight = '0';
      answer.classList.remove('is-open');
    }
  };


  /* ============================================================
     5. DOCUMENT READY — BOOTSTRAP ALL MODULES
     ============================================================ */

  function bootstrap() {
    NesticoStickyATC.init();
    NesticoATCAttention.init();
    NesticoTrackOrder.init();
    NesticoFAQ.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Re-init on Shopify section load (theme editor live preview)
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target;
    if (section.querySelector('.nestico-track-order')) {
      NesticoTrackOrder.init();
    }
    if (section.querySelector('.nestico-faq-item')) {
      NesticoFAQ.init();
    }
  });

  // Expose for external use / debugging
  window.Nestico = window.Nestico || {};
  window.Nestico.StickyATC    = NesticoStickyATC;
  window.Nestico.ATCAttention = NesticoATCAttention;
  window.Nestico.TrackOrder   = NesticoTrackOrder;
  window.Nestico.FAQ          = NesticoFAQ;

})();
